import { useEffect, useState } from 'react'
import { faHexagonNodes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export type LoadingCaption = {
    caption: string
    durationMSs?: number
}

interface Props {
    show?: boolean
    fullScreen?: boolean
    captions?: LoadingCaption[]
    className?: string
    spinnerSize?: number | string // New prop for dynamic spinner size
    // New props for customizing colours:
    outerCircleClass?: string // For the outer circle’s border color
    iconClass?: string // For the inner icon's color
}

function LoadingSpinner({
    show = true,
    fullScreen = false,
    captions = [],
    spinnerSize = '40px',
    outerCircleClass, // optional class to override default outer border color
    iconClass, // optional class to override default icon color
}: Props): React.JSX.Element {
    const [captionIndex, setCaptionIndex] = useState(0)

    useEffect(() => {
        if (!captions.length || captionIndex >= captions.length - 1) return

        const currentCaption = captions[captionIndex]
        const duration = currentCaption.durationMSs ?? 3000 // Default to 3s if no duration is provided

        const timer = setTimeout(() => {
            setCaptionIndex((prevIndex) => prevIndex + 1)
        }, duration)

        return () => clearTimeout(timer) // Cleanup on unmount or update
    }, [captionIndex, captions])

    if (!show) {
        return <></>
    }

    if (fullScreen) {
        return (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-500 bg-opacity-20">
                <LoadingSpinner
                    show={true}
                    fullScreen={false}
                    captions={captions}
                    spinnerSize={spinnerSize}
                    outerCircleClass={outerCircleClass}
                    iconClass={iconClass}
                />
            </div>
        )
    }

    // Convert spinnerSize to a valid CSS size string if a number is provided.
    const size =
        typeof spinnerSize === 'number' ? `${spinnerSize}px` : spinnerSize
    // Determine a numeric value for the spinner size (assumes px) to calculate icon font size.
    const numericSize =
        typeof spinnerSize === 'number'
            ? spinnerSize
            : parseInt(spinnerSize) || 40
    // Set the icon's font size to 60% of the spinner's size.
    const iconFontSize = numericSize * 0.6

    // Default colour classes if not provided.
    const defaultOuterColor = 'border-ctxt-blue'
    const defaultIconColor = 'text-ctxt-blue'

    return (
        <div className="flex flex-col justify-center items-center h-full w-full space-y-4">
            {/* Spinning outer circle with icon inside */}
            <div className="relative">
                <div
                    className={`animate-spin rounded-full border-t-2 border-opacity-75 ${
                        outerCircleClass ? outerCircleClass : defaultOuterColor
                    }`}
                    style={{ height: size, width: size }}
                ></div>
                <FontAwesomeIcon
                    icon={faHexagonNodes}
                    style={{ fontSize: iconFontSize }}
                    className={`absolute inset-0 m-auto p-1 rounded-lg opacity-75 ${
                        iconClass ? iconClass : defaultIconColor
                    }`}
                />
            </div>

            {/* Caption */}
            {captions.length > 0 && (
                <p
                    className="text-lg text-center 
                               inline-block 
                               bg-narrow-spotlight
                               bg-[length:700px_100%] 
                               animate-shimmer 
                               text-transparent 
                               bg-clip-text"
                >
                    {captions[captionIndex].caption}
                </p>
            )}
        </div>
    )
}

export default LoadingSpinner
